import React from 'react';
import {
  Paper,
  Divider,
  Grid,
  Box,
  Container,
  FormControl,
  Checkbox,
  Button,
  Select,
  Stack,
  MenuItem
} from '@mui/material';
import api from "../../services/api";
import {
  Autocomplete,
  DateField
} from '../../components';
import { useStoreActions } from 'easy-peasy';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { pt } from 'yup-locale-pt';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import ptBRLocale from 'date-fns/locale/pt-BR';
import autocolors from 'chartjs-plugin-autocolors';
import zoomPlugin from 'chartjs-plugin-zoom';
import { novosIndicadores, indicadoresComTotal } from './static';
yup.setLocale(pt);
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Graficos = () => {
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);

  const [empresas, setEmpresas] = React.useState([]);
  const [cnaes, setCnaes] = React.useState([]);
  const [temas, setTemas] = React.useState([]);
  const [indicadores, setIndicadores] = React.useState([]);
  const [minDate, setMinDate] = React.useState(null);
  const [maxDate, setMaxDate] = React.useState(null);
  const [datesDisabled, setDatesDisabled] = React.useState(true);
  const [datasets, setDatasets] = React.useState([]);
  const [chartType, setChartType] = React.useState('line');
  const [chartTitle, setChartTitle] = React.useState('Indicador');

  const serieOptions = [{
    id: 'empresas',
    label: 'Empresa'
  },{
    id: 'cnaes',
    label: 'CNAE'
  },{
    id: 'total',
    label: 'Total'
  }];

  const agrupamentos = [{
    id: 'mes',
    label: 'Mes'
  },{
    id: 'ano',
    label: 'Ano'
  }];

  const schema = yup.object({
    serie: yup.string()
      .nullable()
      .oneOf(serieOptions.map((serie)=>serie.id))
      .label('Série')
      .required(),
    agrupamento: yup.string()
      .nullable()
      .oneOf(agrupamentos.map((agrupamento)=>agrupamento.id))
      .label('Agrupamento')
      .required(),
    tema: yup.string()
      .nullable()
      .label('Tema')
      .required(),
    indicador: yup.string()
      .nullable()
      .label('Indicador')
      .required(),
    empresas: yup.array()
      .of(yup.number().positive())
      .label('Empresa(s)'),
    cnaes: yup.array()
      .of(yup.number().positive())
      .label('CNAE(s)'),
    de: yup.date().label('De').required(),
    ate: yup.date().label('Até').required(),
  }).required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      serie: 'empresas',
      agrupamento: 'mes',
      indicador: '',
      tema: '',
      de: null,
      ate: null
    }
  });

  const { handleSubmit, setValue,
          watch, formState: {isDirty,isSubmitting}
        } = useFormRef;

  const selectedCnaes = watch('cnaes');
  const selectedEmpresas = watch('empresas');
  const indicador = watch('indicador');

  // load empresas
  React.useEffect(()=> {
    let mount = {
      isMounted: true,
    };
    if(!indicador) {
      setEmpresas([]);
    } else {
      loadEmpresas(mount);
    }
    return () => {
      mount.isMounted = false;
    };
  },[indicador,selectedCnaes]);

  const loadEmpresas = async (mount) => {
    startLoading();
    try {
      let data = (await api.http.get('/empresas',{
        params: {
          all: indicador!='riscoRelativoCidF',
          cnaes: selectedCnaes?selectedCnaes:null
        }
      })).data;

      if (mount.isMounted) {
        setEmpresas(data);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    };
  };

  // load cnaes
  React.useEffect(() => {
    async function fetchCnaes() {
      startLoading();
      try {
        const response = await api.http.get('/cnaes', {
          params: {
            empresas: selectedEmpresas ? selectedEmpresas : null,
          },
        });
        setCnaes(response.data);
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: 'error',
        });
      } finally {
        stopLoading();
      }
    }
    fetchCnaes();
  }, [selectedEmpresas]);

  React.useEffect(() => {
    let mount = {
      isMounted: true,
    };
    loadIndicadores(mount);
    loadTemas(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  const loadIndicadores = async (mount) => {
    startLoading();
    try {
      let data = (await api.http.get("/indicadores")).data;
      if (mount.isMounted) {
        // indicadores mapeados por tema (lista) e por id
        setIndicadores(data.reduce((map,ind)=>{
          if(!(ind.temaId in map['porTema'])) {
            map['porTema'][ind.temaId] = [];
          }
          map['porTema'][ind.temaId].push(ind);

          if(!(ind.id in map['porId'])) {
            map['porId'][ind.id] = ind;
          }
          return map;
        },{ porTema: {}, porId: {}}));
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: "error",
      });
      throw error;
    }
  };

  const loadTemas = async (mount) => {
    startLoading();
    try {
      let data = (await api.http.get("/temas")).data;
      const filteredData = data.filter((tema) => !['03a', '03b'].includes(tema.id));
      if (mount.isMounted) {
        setTemas(filteredData);
      }
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: "error",
      });
      throw error;
    }
  };

  // load datas
  const deField = watch('de');
  const ateField = watch('ate');
  const serie = watch('serie');
  const agrupamento = watch('agrupamento');
  const tema = watch('tema');

  React.useEffect(()=>{
    setValue('indicador','');
    if(tema=='03b') {
      setValue('cnaes',[]);
      setValue('empresas',[]);
      if(serie=='cnaes')
        setValue('serie','empresas');
    }
  },[tema]);


  React.useEffect(()=>{
    if(deField && ateField && deField>ateField) {
      setValue('ate',deField);
    }
  },[deField]);

  React.useEffect(() => {
    if (!agrupamento) return;
  
    async function fetchDates() {
      startLoading();
      try {
        const response = await api.http.post('/indicadores/datas', {
          agrupamento: agrupamento,
          cnaes: selectedCnaes || null,
          empresas: selectedEmpresas || null,
          indicador: indicador || null,
        });
  
        const datas = response.data;
  
        if (datas.minDate && datas.maxDate) {
          const minDateArr = datas.minDate.split('-');
          const maxDateArr = datas.maxDate.split('-');
          const minDate = new Date(minDateArr[0], minDateArr[1] - 1, minDateArr[2]);
          const maxDate = new Date(maxDateArr[0], maxDateArr[1] - 1, maxDateArr[2]);
  
          setMinDate(minDate);
          setMaxDate(maxDate);
  
          if (!deField || deField < minDate) {
            setValue('de', minDate);
          }
          if (!ateField || ateField > maxDate) {
            setValue('ate', maxDate);
          }
          setDatesDisabled(false);
        } else {
          setDatesDisabled(true);
          setValue('de', null);
          setValue('ate', null);
        }
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: 'error',
        });
      } finally {
        stopLoading();
      }
    }
  
    fetchDates();
  }, [selectedCnaes, selectedEmpresas, agrupamento, indicador]);
  


  const submit = async (data) => {
    startLoading();
    try {
      setChartTitle(indicadores['porId'][indicador].nome);

      let newDatasets = (await api.http.post('/indicadores/datasets',{
        ...data,
        de: {
          ano: data.de.getFullYear(),
          mes: data.agrupamento=='mes'?data.de.getMonth()+1:1
        },
        ate: {
          ano: data.ate.getFullYear(),
          mes: data.agrupamento=='mes'?data.ate.getMonth()+1:12
        }
      })).data;
      setDatasets(newDatasets);
      if(newDatasets.length==0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    };
  };

  React.useEffect(()=>{
    if(indicadoresComTotal.includes(indicador)){
      setValue('serie','total');
    }

    if(novosIndicadores.includes(indicador)) {
      setValue('agrupamento','ano');
      setValue('cnaes',[]);
    }

    if(indicador=='riscoRelativoCidF' && ['total','cnaes'].includes(serie)) {
      setValue('serie','empresas');
    }
  },[indicador]);


  ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    LineController,
    BarController,
    Title,
    Tooltip,
    Legend,
    autocolors,
    zoomPlugin
  );


  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: chartTitle,
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'ctrl',
        },
        zoom: {
          drag: {
            enabled: true,
          },
          mode: 'x',
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: agrupamento=='mes'?'month':'year'
        },
        adapters: {
          date: {
            locale: ptBRLocale
          }
        }
      },
      y: {
        min: 0
      }
    }

  };

  const chartRef = React.useRef(null);
  const resetZoom = () => {
    chartRef.current.resetZoom();
  };

  const onChangeType = (event) => {
    setChartType(event.target.value);
  };

  const getSerieOptions = () => {
    if(indicadoresComTotal.includes(indicador)) {
      return serieOptions.filter(serie=>serie.id =='total');

    } else if(indicador=='riscoRelativoCidF') {
      return serieOptions.filter(serie=>!['total','cnaes'].includes(serie.id));

    } else if (tema=='03b') {
      return serieOptions.filter(serie=>!['cnaes'].includes(serie.id));

    } else {
      return serieOptions;
    }
  };

  React.useEffect(() => {
    async function updateChart() {
      if (chartRef?.current) {
        chartRef.current.update();
      }
    }
    updateChart();
  }, [chartType]);
  return (
    <Container maxWidth="false">

      <Paper variant="outlined" className='center-box' sx={{
        textAlign: 'center',
        px: 4,
        pt: 2,
        pb: 3,
        m: 5,
      }}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(submit)}
        >
          <Divider>
            <h3>Gráficos</h3>
          </Divider>
          <Stack sx={{mb:4}} direction="row" spacing={2} justifyContent="center">
            <FormControl sx={{minWidth:180}} >
              <Autocomplete
                field="serie"
                useFormRef={useFormRef}
                options={getSerieOptions()}
                schema={schema}  />
            </FormControl>
            <FormControl sx={{minWidth:180}} >
              <Autocomplete
                field="agrupamento"
                useFormRef={useFormRef}
                options={novosIndicadores.includes(indicador)?agrupamentos.filter(agr => agr.id == 'ano'):agrupamentos}
                schema={schema}  />
            </FormControl>
          </Stack>
          <Stack sx={{mb:4}} direction="row" spacing={2} justifyContent="center">
            <FormControl fullWidth>
              <Autocomplete
                field="tema"
                useFormRef={useFormRef}
                options={'porTema' in indicadores ?
                         temas.filter(tema=>tema.id in indicadores['porTema']):[]}
                getOptionLabel={(tema) => tema.id + ' - ' + tema.name}
                schema={schema} />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                field="indicador"
                useFormRef={useFormRef}
                options={tema ? (
                  agrupamento=='mes'?
                    indicadores['porTema'][tema]:
                    indicadores['porTema'][tema].filter(
                      ind=>!['absenteismo',
                             'absenteismoCidF'].includes(ind.id))):[]}
                getOptionLabel={(ind) => ind.nome}
                schema={schema} />
            </FormControl>
          </Stack>
          { tema!='03b' &&
            <Stack sx={{mb:4}} direction="row" spacing={2} justifyContent="center">
              <FormControl fullWidth >
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  field="empresas"
                  useFormRef={useFormRef}
                  options={empresas}
                  getOptionLabel={(empresa) => empresa.nome+' ('+empresa.cnpj+')'}
                  renderOption={(props, option, { selected }) => {
                    const {key, ...restProps} = props;
                    return(
                      <li key={key} {...restProps}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {props.key}
                      </li>
                    );
                  }}
                  schema={schema}  />
              </FormControl>

              { !novosIndicadores.includes(indicador) &&
                <FormControl fullWidth >
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    field="cnaes"
                    useFormRef={useFormRef}
                    options={cnaes}
                    getOptionLabel={(cnae) => cnae.codigo + ' - ' + cnae.descr}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {props.key}
                        </li>
                      );
                    }}
                    schema={schema} />
                </FormControl>

              }
            </Stack>
          }
          <Stack direction="row" spacing={2} justifyContent="center">
            <FormControl >
              <DateField
                field="de"
                views={agrupamento=='mes'?['year','month']:['year']}
                minDate={minDate}
                maxDate={maxDate}
                disabled={datesDisabled}
                inputFormat={agrupamento=='mes'?"MM/yyyy":"yyyy"}
                useFormRef={useFormRef}
                schema={schema} />
            </FormControl>
            <FormControl  >
              <DateField
                field="ate"
                views={agrupamento=='mes'?['year','month']:['year']}
                minDate={deField?deField:minDate}
                maxDate={maxDate}
                disabled={datesDisabled}
                inputFormat={agrupamento=='mes'?"MM/yyyy":"yyyy"}
                useFormRef={useFormRef}
                schema={schema} />
            </FormControl>
            <Button type="submit"
                    variant="contained"
                    disabled={isSubmitting}>
              Plotar
            </Button>
          </Stack>
        </Box>
      </Paper>

      {datasets.length>0 &&

       <Paper variant="outlined" className='center-box' sx={{
         textAlign: 'center',
         px: 4,
         pt: 2,
         pb: 3,
         m: 5,
       }}>
         <Chart
           type={chartType}
           options={options}
           data={{
             datasets: datasets.map((data)=>({
               label: data.label,
               data: data.data,
               type: chartType
             }))
           }}
           ref={chartRef}
         />
         <Stack direction="row" spacing={2} sx={{mt:2}} justifyContent="center">
           <Button onClick={()=>{resetZoom();}}>Reset Zoom</Button>
           <Select
             label="Tipo de gráfico"
             value={chartType}
             onChange={onChangeType}
           >
             <MenuItem value="line">Linha</MenuItem>
             <MenuItem value="bar">Barra</MenuItem>
           </Select>
         </Stack>
       </Paper>
      }
    </Container>
  );
};

export default Graficos;
